import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';
import { Publication, PublicationUpdateInput, PublicationOptions, Feature } from './types';

// ========================================
// Package Feature Mappings (mirrors Rails Package and ServicesPackage models)
// ========================================

// Base features included in all product packages
const BASE_PACKAGE_FEATURES = [
  'analytics_return_on_investment',
  'detailed_balance',
  'download_pitches_csv',
  'download_stories_csv',
  'download_story',
  'download_story_activity',
  'overdraft_blocking',
  'standard_roles_only',
];

// Content Generation package additional features
const CONTENT_GENERATION_FEATURES = [
  'analytics_production',
  'content_strategy',
  'content_assist',
  'fields',
  'quick_edit',
  'recommended_contributor',
  'social_posts',
  'story_attributes', // TAGS
];

// Efficiency package additional features
const EFFICIENCY_FEATURES = [
  'approval_flow',
  'brief_templates',
  'campaign',
  'command_center',
  'content_portal',
  'content_requests',
  'dashboard',
  'dashboard_beta',
  'integrations_aem',
  'media_library',
  'remote_approval',
  'salesforce',
  'user_activity_metrics',
  'seo_story_ideas',
];

// Optimization package additional features
const OPTIMIZATION_FEATURES = [
  'analytics_performance',
  'analytics_documents',
  'analytics_email',
  'analytics_social_stats',
  'documents',
];

// Product package feature mappings (cumulative)
const PRODUCT_PACKAGE_FEATURE_MAPPING: Record<string, string[]> = {
  content_generation: [...BASE_PACKAGE_FEATURES, ...CONTENT_GENERATION_FEATURES],
  efficiency: [...BASE_PACKAGE_FEATURES, ...CONTENT_GENERATION_FEATURES, ...EFFICIENCY_FEATURES],
  optimization: [...BASE_PACKAGE_FEATURES, ...CONTENT_GENERATION_FEATURES, ...EFFICIENCY_FEATURES, ...OPTIMIZATION_FEATURES],
};

// Services package feature mappings
const MANAGEMENT_FEATURES = ['create_talent_request', 'invite_talent'];
const PLACEMENT_FEATURES = ['request_more_talent'];

const SERVICES_PACKAGE_FEATURE_MAPPING: Record<string, string[]> = {
  management: [...MANAGEMENT_FEATURES],
  placement: [...MANAGEMENT_FEATURES, ...PLACEMENT_FEATURES],
};

// Get all features that belong to any product package
const ALL_PRODUCT_PACKAGE_FEATURES = [
  ...BASE_PACKAGE_FEATURES,
  ...CONTENT_GENERATION_FEATURES,
  ...EFFICIENCY_FEATURES,
  ...OPTIMIZATION_FEATURES,
];

// Get all features that belong to any services package
const ALL_SERVICES_PACKAGE_FEATURES = [...MANAGEMENT_FEATURES, ...PLACEMENT_FEATURES];

// ========================================
// Types for Autocomplete
// ========================================

interface AutocompleteAccount {
  id: number;
  text: string;
}

interface AutocompleteUser {
  id: number;
  name: string;
  email: string;
  avatar_url: string;
  text: string;
}

interface ContentStrategyOption {
  id: number;
  label: string;
}

// ========================================
// Styled Components
// ========================================

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;

  a {
    color: #00a6c2;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: right;

  a {
    color: #00a6c2;
    text-decoration: none;
    font-size: 14px;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#00a6c2' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  margin-bottom: -1px;
  transition: all 0.2s;

  &:hover {
    color: #00a6c2;
    background: #f9fafb;
  }
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FormColumn = styled.div<{ $flex?: number }>`
  flex: ${({ $flex }) => $flex || 1};
  min-width: 0;
`;

const Label = styled.label<{ $required?: boolean }>`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;

  ${({ $required }) =>
    $required &&
    `
    &::after {
      content: ' *';
      color: #ef4444;
    }
  `}
`;

const SubText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00a6c2;
    box-shadow: 0 0 0 3px rgba(0, 166, 194, 0.1);
  }

  &:disabled,
  &[readonly] {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00a6c2;
    box-shadow: 0 0 0 3px rgba(0, 166, 194, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00a6c2;
    box-shadow: 0 0 0 3px rgba(0, 166, 194, 0.1);
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  padding: 6px 0;

  &:hover {
    color: #111827;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  gap: 6px;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.label`
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  padding: 4px 0;

  input {
    margin-top: 2px;
  }

  &:hover {
    color: #111827;
  }
`;

const FeatureInfo = styled.span`
  color: #9ca3af;
  margin-left: 4px;
  cursor: help;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  ${({ $variant = 'secondary' }) =>
    $variant === 'primary'
      ? 'background: #00a6c2; color: white; &:hover { background: #0094ab; }'
      : 'background: #e5e7eb; color: #374151; &:hover { background: #d1d5db; }'}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Toast = styled.div<{ $type: 'success' | 'error' }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  animation: slideIn 0.3s ease;

  ${({ $type }) =>
    $type === 'success' ? 'background: #10b981; color: white;' : 'background: #ef4444; color: white;'}

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 48px;
  color: #ef4444;
`;

const AvatarPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  img {
    width: 64px;
    height: 64px;
    border-radius: 4px;
    object-fit: cover;
    border: 1px solid #e5e7eb;
  }
`;

const ReadOnlyValue = styled.div`
  padding: 10px 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 14px;

  a {
    color: #00a6c2;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

// Autocomplete Styled Components
const AutocompleteWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const AutocompleteDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const AutocompleteItem = styled.div<{ $isHighlighted?: boolean }>`
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: #111827;
  background: ${props => (props.$isHighlighted ? '#f3f4f6' : 'white')};

  &:hover {
    background: #f3f4f6;
  }
`;

const AutocompleteUserItem = styled.div<{ $isHighlighted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  background: ${props => (props.$isHighlighted ? '#f3f4f6' : 'white')};

  &:hover {
    background: #f3f4f6;
  }
`;

const UserAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  background: #e5e7eb;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
`;

const UserEmail = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const SelectedDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
`;

const SelectedText = styled.span`
  flex: 1;
  font-size: 14px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  font-size: 18px;
  line-height: 1;

  &:hover {
    color: #111827;
  }
`;

const AutocompleteSearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #d1d5db;
  font-size: 14px;
  box-sizing: border-box;
  &:focus {
    outline: none;
  }
`;

const AutocompleteHint = styled.div`
  padding: 10px 14px;
  font-size: 13px;
  color: #6b7280;
  text-align: center;
`;

const InfoIcon = styled.span`
  color: #9ca3af;
  margin-left: 4px;
  cursor: help;
  font-size: 12px;
`;

function AdminPublicationEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Determine if we're in create mode
  const isCreateMode = !id || location.pathname.includes('/new');

  const [publication, setPublication] = useState<Publication | null>(null);
  const [options, setOptions] = useState<PublicationOptions | null>(null);
  const [formData, setFormData] = useState<Partial<PublicationUpdateInput>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Organization/Account autocomplete state
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [orgResults, setOrgResults] = useState<AutocompleteAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AutocompleteAccount | null>(null);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [orgHighlightedIndex, setOrgHighlightedIndex] = useState(-1);
  const orgSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const orgDropdownRef = useRef<HTMLDivElement>(null);

  // Contently Editor autocomplete state
  const [editorSearchQuery, setEditorSearchQuery] = useState('');
  const [editorResults, setEditorResults] = useState<AutocompleteUser[]>([]);
  const [selectedEditor, setSelectedEditor] = useState<AutocompleteUser | null>(null);
  const [showEditorDropdown, setShowEditorDropdown] = useState(false);
  const [editorHighlightedIndex, setEditorHighlightedIndex] = useState(-1);
  const editorSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const editorDropdownRef = useRef<HTMLDivElement>(null);

  // Content Strategy autocomplete state
  const [csSearchQuery, setCsSearchQuery] = useState('');
  const [csFilteredResults, setCsFilteredResults] = useState<ContentStrategyOption[]>([]);
  const [showCsDropdown, setShowCsDropdown] = useState(false);
  const [csHighlightedIndex, setCsHighlightedIndex] = useState(-1);
  const csDropdownRef = useRef<HTMLDivElement>(null);

  const fetchPublication = useCallback(async (pubId: string) => {
    try {
      const response = await api.get(`/admin2/publications/${pubId}.json`);
      setPublication(response.data.publication);
      setOptions(response.data.options);
      
      // Set selected editor if exists
      if (response.data.publication.contentlyEditorId && response.data.publication.contentlyEditorName) {
        setSelectedEditor({
          id: response.data.publication.contentlyEditorId,
          name: response.data.publication.contentlyEditorName,
          email: '',
          avatar_url: '',
          text: response.data.publication.contentlyEditorName
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.errors?.join(', ') || 'Failed to load publication');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNewPublicationOptions = useCallback(async () => {
    try {
      const response = await api.get('/admin2/publications/new.json');
      setOptions(response.data.options);
      // Set default form data for new publication
      const defaultPub = response.data.publication;
      setFormData({
        name: defaultPub.name || '',
        salesforceId: defaultPub.salesforceId || '',
        active: defaultPub.active ?? true,
        internal: defaultPub.internal ?? false,
        billingType: defaultPub.billingType || '',
        contentCommit: defaultPub.contentCommit ?? false,
        perPieceManagementFeePercentage: defaultPub.perPieceManagementFeePercentage ?? 20,
        fixedManagementFeeInDollars: defaultPub.fixedManagementFeeInDollars ?? 0,
        purchaseOrderNumber: defaultPub.purchaseOrderNumber || '',
        billingContacts: defaultPub.billingContacts || '',
        editorialRateInDollars: defaultPub.editorialRateInDollars ?? 0,
        editorialHours: defaultPub.editorialHours ?? 0,
        contentlyEditorId: defaultPub.contentlyEditorId || null,
        nonCompeteAccountIds: defaultPub.nonCompeteAccountIds || [],
        package: defaultPub.package || '',
        servicesPackage: defaultPub.servicesPackage || '',
        featureIds: defaultPub.featureIds || [],
        siteUrl: defaultPub.siteUrl || '',
        documentSubdomain: defaultPub.documentSubdomain || '',
        marketoClientId: defaultPub.marketoClientId || '',
        marketoClientSecret: defaultPub.marketoClientSecret || '',
        marketoHost: defaultPub.marketoHost || '',
        currentContentStrategyId: defaultPub.currentContentStrategyId || null,
        contentStrategyId: defaultPub.currentContentStrategyId || null,
        details: defaultPub.details || '',
        qualityControlCriteria: defaultPub.qualityControlCriteria || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.errors?.join(', ') || 'Failed to load form options');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isCreateMode) {
      fetchNewPublicationOptions();
    } else if (id) {
      fetchPublication(id);
    }
  }, [id, isCreateMode, fetchPublication, fetchNewPublicationOptions]);

  useEffect(() => {
    if (publication && !isCreateMode) {
      setFormData({
        name: publication.name || '',
        salesforceId: publication.salesforceId || '',
        active: publication.active,
        internal: publication.internal,
        billingType: publication.billingType || '',
        contentCommit: publication.contentCommit || false,
        perPieceManagementFeePercentage: publication.perPieceManagementFeePercentage || 20,
        fixedManagementFeeInDollars: publication.fixedManagementFeeInDollars || 0,
        purchaseOrderNumber: publication.purchaseOrderNumber || '',
        billingContacts: publication.billingContacts || '',
        editorialRateInDollars: publication.editorialRateInDollars || 0,
        editorialHours: publication.editorialHours || 0,
        contentlyEditorId: publication.contentlyEditorId || null,
        nonCompeteAccountIds: publication.nonCompeteAccountIds || [],
        package: publication.package || '',
        servicesPackage: publication.servicesPackage || '',
        featureIds: publication.featureIds || [],
        siteUrl: publication.siteUrl || '',
        documentSubdomain: publication.documentSubdomain || '',
        marketoClientId: publication.marketoClientId || '',
        marketoClientSecret: publication.marketoClientSecret || '',
        marketoHost: publication.marketoHost || '',
        currentContentStrategyId: publication.currentContentStrategyId || null,
        details: publication.details || '',
        qualityControlCriteria: publication.qualityControlCriteria || '',
      });
    }
  }, [publication, isCreateMode]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setShowOrgDropdown(false);
      }
      if (editorDropdownRef.current && !editorDropdownRef.current.contains(event.target as Node)) {
        setShowEditorDropdown(false);
      }
      if (csDropdownRef.current && !csDropdownRef.current.contains(event.target as Node)) {
        setShowCsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Create a map of feature name -> feature id for quick lookups
  const featureNameToIdMap = useMemo(() => {
    if (!options?.features) return new Map<string, number>();
    return new Map(options.features.map(f => [f.name, f.id]));
  }, [options?.features]);

  // Get feature IDs for a list of feature names
  const getFeatureIdsByNames = useCallback((featureNames: string[]): number[] => {
    return featureNames
      .map(name => featureNameToIdMap.get(name))
      .filter((id): id is number => id !== undefined);
  }, [featureNameToIdMap]);

  // Handle package change - automatically update features
  const handlePackageChange = useCallback((packageType: 'package' | 'servicesPackage', newValue: string) => {
    const currentFeatureIds = formData.featureIds || [];
    let newFeatureIds = [...currentFeatureIds];

    if (packageType === 'package') {
      // Remove all product package features first
      const productFeatureIds = getFeatureIdsByNames(ALL_PRODUCT_PACKAGE_FEATURES);
      newFeatureIds = newFeatureIds.filter(id => !productFeatureIds.includes(id));

      // Add features for the new package (if selected)
      if (newValue && PRODUCT_PACKAGE_FEATURE_MAPPING[newValue]) {
        const packageFeatureIds = getFeatureIdsByNames(PRODUCT_PACKAGE_FEATURE_MAPPING[newValue]);
        newFeatureIds = [...new Set([...newFeatureIds, ...packageFeatureIds])];
      }
    } else {
      // Services package
      // Remove all services package features first
      const servicesFeatureIds = getFeatureIdsByNames(ALL_SERVICES_PACKAGE_FEATURES);
      newFeatureIds = newFeatureIds.filter(id => !servicesFeatureIds.includes(id));

      // Add features for the new services package (if selected)
      if (newValue && SERVICES_PACKAGE_FEATURE_MAPPING[newValue]) {
        const packageFeatureIds = getFeatureIdsByNames(SERVICES_PACKAGE_FEATURE_MAPPING[newValue]);
        newFeatureIds = [...new Set([...newFeatureIds, ...packageFeatureIds])];
      }
    }

    // Update both the package and featureIds
    setFormData(prev => ({
      ...prev,
      [packageType]: newValue,
      featureIds: newFeatureIds,
    }));
  }, [formData.featureIds, getFeatureIdsByNames]);

  const handleInputChange = (field: keyof PublicationUpdateInput, value: any) => {
    // Special handling for package changes to auto-update features
    if (field === 'package' || field === 'servicesPackage') {
      handlePackageChange(field, value);
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFeatureToggle = (featureId: number) => {
    const currentIds = formData.featureIds || [];
    const newIds = currentIds.includes(featureId)
      ? currentIds.filter((id) => id !== featureId)
      : [...currentIds, featureId];
    handleInputChange('featureIds', newIds);
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Organization/Account autocomplete functions
  const searchOrganizations = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOrgResults([]);
      setOrgHighlightedIndex(-1);
      return;
    }

    try {
      const response = await api.get('/admin2/accounts/autocomplete.json', { params: { query } });
      setOrgResults(response.data || []);
      setOrgHighlightedIndex(-1);
    } catch (error) {
      console.error('Organization autocomplete search failed:', error);
      setOrgResults([]);
    }
  }, []);

  const handleOrgSearchChange = (value: string) => {
    setOrgSearchQuery(value);
    setSelectedAccount(null);

    if (orgSearchTimeoutRef.current) {
      clearTimeout(orgSearchTimeoutRef.current);
    }

    orgSearchTimeoutRef.current = setTimeout(() => {
      searchOrganizations(value);
    }, 300);
  };

  const handleSelectOrg = (account: AutocompleteAccount) => {
    setSelectedAccount(account);
    setOrgSearchQuery('');
    setShowOrgDropdown(false);
    setOrgResults([]);
  };

  const handleClearOrg = () => {
    setSelectedAccount(null);
    setOrgSearchQuery('');
  };

  const handleOrgKeyDown = (e: React.KeyboardEvent) => {
    if (!showOrgDropdown || orgResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOrgHighlightedIndex(prev => (prev < orgResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOrgHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && orgHighlightedIndex >= 0) {
      e.preventDefault();
      handleSelectOrg(orgResults[orgHighlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowOrgDropdown(false);
    }
  };

  // Contently Editor autocomplete functions
  const searchEditors = useCallback(async (query: string) => {
    if (query.length < 2) {
      setEditorResults([]);
      setEditorHighlightedIndex(-1);
      return;
    }

    try {
      const response = await api.get('/admin2/users/autocomplete.json', { params: { query } });
      setEditorResults(response.data || []);
      setEditorHighlightedIndex(-1);
    } catch (error) {
      console.error('Editor autocomplete search failed:', error);
      setEditorResults([]);
    }
  }, []);

  const handleEditorSearchChange = (value: string) => {
    setEditorSearchQuery(value);
    setSelectedEditor(null);
    setFormData(prev => ({ ...prev, contentlyEditorId: null }));

    if (editorSearchTimeoutRef.current) {
      clearTimeout(editorSearchTimeoutRef.current);
    }

    editorSearchTimeoutRef.current = setTimeout(() => {
      searchEditors(value);
    }, 300);
  };

  const handleSelectEditor = (user: AutocompleteUser) => {
    setSelectedEditor(user);
    setEditorSearchQuery('');
    setShowEditorDropdown(false);
    setEditorResults([]);
    setFormData(prev => ({ ...prev, contentlyEditorId: user.id }));
  };

  const handleClearEditor = () => {
    setSelectedEditor(null);
    setEditorSearchQuery('');
    setFormData(prev => ({ ...prev, contentlyEditorId: null }));
  };

  const handleEditorKeyDown = (e: React.KeyboardEvent) => {
    if (!showEditorDropdown || editorResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setEditorHighlightedIndex(prev => (prev < editorResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setEditorHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && editorHighlightedIndex >= 0) {
      e.preventDefault();
      handleSelectEditor(editorResults[editorHighlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowEditorDropdown(false);
    }
  };

  // Content Strategy autocomplete functions
  const handleCsSearchChange = (value: string) => {
    setCsSearchQuery(value);
    const allStrategies = options?.contentStrategies || [];
    if (value.length === 0) {
      setCsFilteredResults(allStrategies);
    } else {
      setCsFilteredResults(
        allStrategies.filter(cs => 
          cs.label.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
    setCsHighlightedIndex(-1);
  };

  const handleSelectCs = (cs: ContentStrategyOption) => {
    handleInputChange('currentContentStrategyId', cs.id);
    setCsSearchQuery('');
    setShowCsDropdown(false);
    setCsFilteredResults([]);
  };

  const handleClearCs = () => {
    handleInputChange('currentContentStrategyId', null);
    setCsSearchQuery('');
  };

  const handleCsKeyDown = (e: React.KeyboardEvent) => {
    if (!showCsDropdown || csFilteredResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCsHighlightedIndex(prev => (prev < csFilteredResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCsHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && csHighlightedIndex >= 0) {
      e.preventDefault();
      handleSelectCs(csFilteredResults[csHighlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowCsDropdown(false);
    }
  };

  const openCsDropdown = () => {
    setShowCsDropdown(true);
    setCsFilteredResults(options?.contentStrategies || []);
  };

  const handleSave = async () => {
    // Validation for create mode
    if (isCreateMode) {
      if (!formData.name?.trim()) {
        showToast('Name is required', 'error');
        return;
      }
      if (!selectedAccount) {
        showToast('Organization is required', 'error');
        return;
      }
      if (!formData.internal && !formData.salesforceId?.trim()) {
        showToast('Salesforce Group ID is required for non-internal publications', 'error');
        return;
      }
      if (!formData.billingType) {
        showToast('Billing type is required', 'error');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        brand_profile: {
          name: formData.name,
          salesforce_id: formData.salesforceId,
          active: formData.active,
          internal: formData.internal,
          billing_type: formData.billingType,
          content_commit: formData.contentCommit,
          per_piece_management_fee_percentage: formData.perPieceManagementFeePercentage,
          fixed_management_fee_in_dollars: formData.fixedManagementFeeInDollars,
          purchase_order_number: formData.purchaseOrderNumber,
          billing_contacts: formData.billingContacts,
          editorial_rate_in_dollars: formData.editorialRateInDollars,
          editorial_hours: formData.editorialHours,
          contently_editor_id: formData.contentlyEditorId,
          package: formData.package,
          services_package: formData.servicesPackage,
          feature_ids: formData.featureIds,
          site_url: formData.siteUrl,
          document_subdomain: formData.documentSubdomain,
          marketo_client_id: formData.marketoClientId,
          marketo_client_secret: formData.marketoClientSecret,
          marketo_host: formData.marketoHost,
          current_content_strategy_id: formData.currentContentStrategyId,
          // content_strategy is only used during create, not update
          ...(isCreateMode ? { content_strategy: formData.contentStrategyId } : {}),
          details: formData.details,
          quality_control_criteria: formData.qualityControlCriteria,
          non_competes_attributes: {
            account_ids: formData.nonCompeteAccountIds || [],
          },
          ...(isCreateMode && selectedAccount ? { account_id: selectedAccount.id } : {}),
        },
      };

      let response;
      const buildFormData = () => {
        const fd = new FormData();
        fd.append('brand_profile[avatar]', avatarFile!);
        Object.entries(payload.brand_profile).forEach(([key, value]) => {
          if (key === 'feature_ids' && Array.isArray(value)) {
            value.forEach(v => fd.append('brand_profile[feature_ids][]', String(v)));
          } else if (key === 'non_competes_attributes') {
            const accountIds = (value as any)?.account_ids || [];
            accountIds.forEach((id: number) => fd.append('brand_profile[non_competes_attributes][account_ids][]', String(id)));
          } else if (value !== undefined && value !== null) {
            fd.append(`brand_profile[${key}]`, String(value));
          }
        });
        return fd;
      };

      if (isCreateMode) {
        if (avatarFile) {
          response = await api.post('/admin2/publications.json', buildFormData(), {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          response = await api.post('/admin2/publications.json', payload);
        }
      } else {
        if (avatarFile) {
          response = await api.put(`/admin2/publications/${id}.json`, buildFormData(), {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          response = await api.put(`/admin2/publications/${id}.json`, payload);
        }
      }

      if (response.data?.success) {
        showToast(isCreateMode ? 'Publication created successfully' : 'Publication saved successfully', 'success');
        if (isCreateMode && response.data.publication?.id) {
          navigate(`/admin2/publications/${response.data.publication.id}/edit`);
        } else {
          setPublication(response.data.publication);
        }
      } else {
        showToast(response.data?.errors?.join(', ') || 'Failed to save publication', 'error');
      }
    } catch (err: any) {
      showToast(err.response?.data?.errors?.join(', ') || 'Failed to save publication', 'error');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <AdminLayout title="Publications" pageTitle={isCreateMode ? 'Create a publication' : 'Edit Publication'}>
        <LoadingState>Loading {isCreateMode ? 'form options' : 'publication'}...</LoadingState>
      </AdminLayout>
    );
  }

  if (error || (!isCreateMode && !publication)) {
    return (
      <AdminLayout title="Publications" pageTitle={isCreateMode ? 'Create a publication' : 'Edit Publication'}>
        <ErrorState>{error || 'Publication not found'}</ErrorState>
      </AdminLayout>
    );
  }

  const pageTitle = isCreateMode
    ? 'Create a publication'
    : `Edit ${publication?.accountName} - ${publication?.name}`;

  // Get selected content strategy label
  const selectedCsLabel = formData.currentContentStrategyId
    ? options?.contentStrategies.find(cs => cs.id === formData.currentContentStrategyId)?.label
    : null;

  return (
    <AdminLayout title="Publications" pageTitle={isCreateMode ? 'Publications / New' : `Publications / ${publication?.name || ''}`}>
      {/* Breadcrumb */}
      <Breadcrumb>
        <Link to="/admin2/publications">Publications</Link>
        <span>›</span>
        <span>
          {isCreateMode ? 'New' : `${publication?.accountName} - ${publication?.name}`}
        </span>
      </Breadcrumb>

      {/* Page Header */}
      <PageHeader>
        <PageTitle>{pageTitle}</PageTitle>
        {!isCreateMode && (
          <HeaderActions>
            <Link to={`/admin2/publications/${id}/talent_requests`}>Manage talent requests</Link>
            {publication?.currentContentStrategyId && (
              <Link to={`/admin2/content_strategies/${publication.currentContentStrategyId}/edit`}>
                Manage content strategy
              </Link>
            )}
          </HeaderActions>
        )}
      </PageHeader>

      {/* Tab Navigation - only show in edit mode */}
      {!isCreateMode && (
        <TabNav>
          <TabLink to={`/admin2/publications/${id}/edit`} $active>
            Publication info
          </TabLink>
          <TabLink to={`/admin2/publications/${id}/users`}>Users</TabLink>
          <TabLink to={`/admin2/publications/${id}/transactions`}>Transactions</TabLink>
          <TabLink to={`/admin2/publications/${id}/fixed_arr_credits`}>Fixed ARR Credits</TabLink>
          <TabLink to={`/admin2/publications/${id}/bulk_story_tagging/new`}>Bulk Story Tagging</TabLink>
          <TabLink to={`/admin2/publications/${id}/talent_requests`}>Talent Requests</TabLink>
        </TabNav>
      )}

      {/* Name */}
      <FormGroup>
        <Label $required>Name</Label>
        <Input
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleInputChange('name', e.target.value)}
          required
        />
      </FormGroup>

      {/* Salesforce Group ID */}
      <FormGroup>
        <Label $required={!formData.internal}>
          SalesForce Group ID (required if publication is not internal)
        </Label>
        <Input
          type="text"
          value={formData.salesforceId || ''}
          onChange={(e) => handleInputChange('salesforceId', e.target.value)}
          required={!formData.internal}
        />
      </FormGroup>

      {/* Status */}
      <FormSection>
        <SectionTitle>Status</SectionTitle>
        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            checked={formData.active || false}
            onChange={(e) => handleInputChange('active', e.target.checked)}
          />
          Active
        </CheckboxLabel>
        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            checked={formData.internal || false}
            onChange={(e) => handleInputChange('internal', e.target.checked)}
          />
          Internal
        </CheckboxLabel>
      </FormSection>

      {/* Management */}
      <FormSection>
        <SectionTitle>Management</SectionTitle>

        {/* Logo */}
        <FormGroup>
          <Label>Logo</Label>
          <AvatarPreview>
            {(avatarPreview || publication?.avatarUrl) && (
              <img src={avatarPreview || publication?.avatarUrl} alt="Logo" />
            )}
            <Input
              type="file"
              accept="image/jpeg,image/png,image/gif"
              onChange={handleAvatarChange}
              style={{ width: 'auto' }}
            />
          </AvatarPreview>
        </FormGroup>

        {/* Organization - Typeahead Autocomplete */}
        <FormGroup>
          <Label $required>Organization</Label>
          {isCreateMode ? (
            <AutocompleteWrapper ref={orgDropdownRef}>
              {selectedAccount ? (
                <SelectedDisplay>
                  <SelectedText>{selectedAccount.text}</SelectedText>
                  <ClearButton onClick={handleClearOrg} title="Clear selection">×</ClearButton>
                </SelectedDisplay>
              ) : (
                <>
                  <Input
                    type="text"
                    placeholder="Account"
                    value={orgSearchQuery}
                    onChange={(e) => handleOrgSearchChange(e.target.value)}
                    onFocus={() => setShowOrgDropdown(true)}
                    onKeyDown={handleOrgKeyDown}
                  />
                  {showOrgDropdown && (
                    <AutocompleteDropdown>
                      {orgSearchQuery.length < 2 ? (
                        <AutocompleteHint>Please enter 2 or more characters</AutocompleteHint>
                      ) : orgResults.length === 0 ? (
                        <AutocompleteHint>No results found</AutocompleteHint>
                      ) : (
                        orgResults.map((account, index) => (
                          <AutocompleteItem
                            key={account.id}
                            $isHighlighted={index === orgHighlightedIndex}
                            onClick={() => handleSelectOrg(account)}
                          >
                            {account.text}
                          </AutocompleteItem>
                        ))
                      )}
                    </AutocompleteDropdown>
                  )}
                </>
              )}
            </AutocompleteWrapper>
          ) : (
            <ReadOnlyValue>
              <Link to={`/admin2/accounts/${publication?.accountId}/edit`}>{publication?.accountName}</Link>
            </ReadOnlyValue>
          )}
        </FormGroup>

        {/* Billing type */}
        <FormGroup>
          <Label $required>Billing type</Label>
          <Select
            value={formData.billingType || ''}
            onChange={(e) => handleInputChange('billingType', e.target.value)}
            required
          >
            <option value="">Select one</option>
            {options?.billingTypes.map((bt) => (
              <option key={bt.value} value={bt.value}>
                {bt.label}
              </option>
            ))}
          </Select>
        </FormGroup>

        {/* Content commit status */}
        <FormGroup>
          <Label>Content commit status</Label>
          <SubText>
            Indicate whether or not this publication has committed to a content spend bracket. Customers who commit to
            content spend pay their Contently margin directly to the finance team and not in the product.
          </SubText>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                name="contentCommit"
                checked={formData.contentCommit === true}
                onChange={() => handleInputChange('contentCommit', true)}
              />
              Yes
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                name="contentCommit"
                checked={formData.contentCommit === false}
                onChange={() => handleInputChange('contentCommit', false)}
              />
              No
            </RadioLabel>
          </RadioGroup>
        </FormGroup>

        {/* Contently content margin % */}
        <FormGroup>
          <Label>Contently content margin %</Label>
          <SubText>
            This is the Contently content margin added to every payment on a piece of content created in the product.
            Changing this margin will only affect new payments.
          </SubText>
          <Input
            type="number"
            min="0"
            max="100"
            value={formData.perPieceManagementFeePercentage || 0}
            onChange={(e) => handleInputChange('perPieceManagementFeePercentage', Number(e.target.value))}
            readOnly={formData.contentCommit}
          />
        </FormGroup>

        {/* Fixed management fee */}
        <FormGroup>
          <Label>Fixed management fee</Label>
          <Input
            type="number"
            min="0"
            value={formData.fixedManagementFeeInDollars || 0}
            onChange={(e) => handleInputChange('fixedManagementFeeInDollars', Number(e.target.value))}
          />
        </FormGroup>

        {/* Purchase order number */}
        <FormGroup>
          <Label>Purchase order number</Label>
          <Input
            type="text"
            value={formData.purchaseOrderNumber || ''}
            onChange={(e) => handleInputChange('purchaseOrderNumber', e.target.value)}
          />
        </FormGroup>

        {/* Billing contacts' email */}
        <FormGroup>
          <Label>Billing contacts' email</Label>
          <SubText>If left blank, will be sent to all admins of publication. Separate by commas.</SubText>
          <Input
            type="text"
            value={formData.billingContacts || ''}
            onChange={(e) => handleInputChange('billingContacts', e.target.value)}
          />
        </FormGroup>

        {/* Editorial services: hourly rate */}
        <FormGroup>
          <Label>Editorial services: hourly rate $</Label>
          <Input
            type="number"
            min="0"
            value={formData.editorialRateInDollars || 0}
            onChange={(e) => handleInputChange('editorialRateInDollars', Number(e.target.value) || 0)}
          />
        </FormGroup>

        {/* Editorial services: hours per month */}
        <FormGroup>
          <Label>Editorial services: hours per month</Label>
          <Input
            type="number"
            min="0"
            value={formData.editorialHours || 0}
            onChange={(e) => handleInputChange('editorialHours', Number(e.target.value) || 0)}
          />
        </FormGroup>

        {/* Contently editor - Typeahead Autocomplete */}
        <FormGroup>
          <Label>Contently editor</Label>
          <AutocompleteWrapper ref={editorDropdownRef}>
            {selectedEditor ? (
              <SelectedDisplay>
                <SelectedText>{selectedEditor.name || selectedEditor.text}</SelectedText>
                <ClearButton onClick={handleClearEditor} title="Clear selection">×</ClearButton>
              </SelectedDisplay>
            ) : (
              <>
                <Input
                  type="text"
                  placeholder="Start typing to find a user"
                  value={editorSearchQuery}
                  onChange={(e) => handleEditorSearchChange(e.target.value)}
                  onFocus={() => setShowEditorDropdown(true)}
                  onKeyDown={handleEditorKeyDown}
                />
                {showEditorDropdown && (
                  <AutocompleteDropdown>
                    {editorSearchQuery.length < 2 ? (
                      <AutocompleteHint>Please enter 2 or more characters</AutocompleteHint>
                    ) : editorResults.length === 0 ? (
                      <AutocompleteHint>No results found</AutocompleteHint>
                    ) : (
                      editorResults.map((user, index) => (
                        <AutocompleteUserItem
                          key={user.id}
                          $isHighlighted={index === editorHighlightedIndex}
                          onClick={() => handleSelectEditor(user)}
                        >
                          <UserAvatar
                            src={user.avatar_url || '/images/default_avatar.png'}
                            alt={user.name}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/images/default_avatar.png';
                            }}
                          />
                          <UserInfo>
                            <UserName>{user.name}</UserName>
                            <UserEmail>{user.email}</UserEmail>
                          </UserInfo>
                        </AutocompleteUserItem>
                      ))
                    )}
                  </AutocompleteDropdown>
                )}
              </>
            )}
          </AutocompleteWrapper>
        </FormGroup>

        {/* Contributor exclusivity - Multi-select Dropdown */}
        <FormGroup>
          <Label>
            Contributor exclusivity
            <InfoIcon title="Contributors working in any organizations listed here will be ineligible to work for this publication. Similarly, contributors working on this publication will be ineligible to work in any organizations listed below.">ⓘ</InfoIcon>
          </Label>
          <Select
            multiple
            value={(formData.nonCompeteAccountIds || []).map(String)}
            onChange={(e) => {
              const selectedOptions = Array.from(e.target.selectedOptions, option => Number(option.value));
              handleInputChange('nonCompeteAccountIds', selectedOptions);
            }}
            style={{ minHeight: '120px' }}
          >
            {options?.accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.name}
              </option>
            ))}
          </Select>
          <SubText>Hold Ctrl/Cmd to select multiple accounts</SubText>
        </FormGroup>

        {/* Product package */}
        <FormGroup>
          <Label>Product package</Label>
          <SubText>
            Changing a publication's product package will alter the associated features to match those within the
            package.
          </SubText>
          <Select
            value={formData.package || ''}
            onChange={(e) => handleInputChange('package', e.target.value)}
          >
            <option value="">Select a product package</option>
            {options?.packages.map((pkg) => (
              <option key={pkg.value} value={pkg.value}>
                {pkg.label}
              </option>
            ))}
          </Select>
        </FormGroup>

        {/* Services package */}
        <FormGroup>
          <Label>Services package</Label>
          <SubText>
            Changing a publication's services package will alter the associated features to match those within the
            services package.
          </SubText>
          <Select
            value={formData.servicesPackage || ''}
            onChange={(e) => handleInputChange('servicesPackage', e.target.value)}
          >
            <option value="">Select a services package</option>
            {options?.servicesPackages.map((pkg) => (
              <option key={pkg.value} value={pkg.value}>
                {pkg.label}
              </option>
            ))}
          </Select>
        </FormGroup>

        {/* Features */}
        <FormGroup>
          <Label>Features</Label>
          <FeaturesGrid>
            {options?.features.map((feature: Feature) => (
              <FeatureItem key={feature.id}>
                <Checkbox
                  type="checkbox"
                  checked={(formData.featureIds || []).includes(feature.id)}
                  onChange={() => handleFeatureToggle(feature.id)}
                />
                <span>
                  {feature.label}
                  {feature.description && <FeatureInfo title={feature.description}>ⓘ</FeatureInfo>}
                </span>
              </FeatureItem>
            ))}
          </FeaturesGrid>
        </FormGroup>
      </FormSection>

      {/* Publish & Tracking */}
      <FormSection>
        <SectionTitle>Publish & tracking</SectionTitle>

        <FormGroup>
          <Label>Site url</Label>
          <Input
            type="text"
            value={formData.siteUrl || ''}
            onChange={(e) => handleInputChange('siteUrl', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Document subdomain</Label>
          <Input
            type="text"
            value={formData.documentSubdomain || ''}
            onChange={(e) => handleInputChange('documentSubdomain', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Marketo client</Label>
          <Input
            type="text"
            value={formData.marketoClientId || ''}
            onChange={(e) => handleInputChange('marketoClientId', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Marketo client secret</Label>
          <Input
            type="text"
            value={formData.marketoClientSecret || ''}
            onChange={(e) => handleInputChange('marketoClientSecret', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Marketo host</Label>
          <Input
            type="text"
            value={formData.marketoHost || ''}
            onChange={(e) => handleInputChange('marketoHost', e.target.value)}
          />
        </FormGroup>
      </FormSection>

      {/* Content Strategy - Typeahead Autocomplete */}
      <FormSection>
        <SectionTitle>Content Strategy</SectionTitle>

        <FormGroup>
          <AutocompleteWrapper ref={csDropdownRef}>
            {selectedCsLabel ? (
              <SelectedDisplay>
                <SelectedText>{selectedCsLabel}</SelectedText>
                <ClearButton onClick={handleClearCs} title="Clear selection">×</ClearButton>
              </SelectedDisplay>
            ) : (
              <>
                <Input
                  type="text"
                  placeholder="Select a content strategy"
                  value={csSearchQuery}
                  onChange={(e) => handleCsSearchChange(e.target.value)}
                  onFocus={openCsDropdown}
                  onKeyDown={handleCsKeyDown}
                />
                {showCsDropdown && (
                  <AutocompleteDropdown>
                    <AutocompleteSearchInput
                      type="text"
                      placeholder="Search..."
                      value={csSearchQuery}
                      onChange={(e) => handleCsSearchChange(e.target.value)}
                      autoFocus
                    />
                    {csFilteredResults.length === 0 ? (
                      <AutocompleteHint>No content strategies found</AutocompleteHint>
                    ) : (
                      csFilteredResults.map((cs, index) => (
                        <AutocompleteItem
                          key={cs.id}
                          $isHighlighted={index === csHighlightedIndex}
                          onClick={() => handleSelectCs(cs)}
                        >
                          {cs.label}
                        </AutocompleteItem>
                      ))
                    )}
                  </AutocompleteDropdown>
                )}
              </>
            )}
          </AutocompleteWrapper>
        </FormGroup>
      </FormSection>

      {/* Miscellaneous */}
      <FormSection>
        <SectionTitle>Miscellaneous</SectionTitle>

        <FormGroup>
          <Label>Details</Label>
          <Textarea
            value={formData.details || ''}
            onChange={(e) => handleInputChange('details', e.target.value)}
            rows={5}
          />
        </FormGroup>

        <FormGroup>
          <Label>Quality Control Criteria</Label>
          <Textarea
            value={formData.qualityControlCriteria || ''}
            onChange={(e) => handleInputChange('qualityControlCriteria', e.target.value)}
            rows={5}
          />
        </FormGroup>
      </FormSection>

      {/* Buttons */}
      <ButtonGroup>
        <Button onClick={() => navigate('/admin2/publications')}>Cancel</Button>
        <Button $variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </ButtonGroup>

      {toast && <Toast $type={toast.type}>{toast.message}</Toast>}
    </AdminLayout>
  );
}

export default AdminPublicationEditPage;
